import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card } from 'react-daisyui'
import { useForm } from 'react-hook-form'
import { ToastContainer, toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import {
    AiOutlineArrowLeft,
    AiOutlineEnvironment,
    AiOutlineLoading3Quarters,
    AiOutlineSearch
} from 'react-icons/ai'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import Swal from 'sweetalert2'
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
})

const DEFAULT_CENTER = { lat: -6.2, lng: 106.816666 }
const DEFAULT_ZOOM = 13

const toCoordinateNumber = (value) => {
    if (value === null || value === undefined || value === '') return null
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return null
    return numericValue
}

const isValidCoordinate = (latitude, longitude) =>
    latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180

const parseCoordinateString = (coordinateValue) => {
    if (typeof coordinateValue !== 'string') return null
    const [latitudeText, longitudeText] = coordinateValue.split(',').map((value) => value.trim())
    if (!latitudeText || !longitudeText) return null

    const latitude = toCoordinateNumber(latitudeText)
    const longitude = toCoordinateNumber(longitudeText)
    if (latitude === null || longitude === null) return null
    if (!isValidCoordinate(latitude, longitude)) return null

    return { lat: latitude, lng: longitude }
}

const getAddressCoordinate = (addressData) => {
    const fromCoordinateString = parseCoordinateString(addressData?.coordinate)
    if (fromCoordinateString) return fromCoordinateString

    const latitude =
        toCoordinateNumber(addressData?.latitude) ??
        toCoordinateNumber(addressData?.lat) ??
        toCoordinateNumber(addressData?.address_latitude) ??
        toCoordinateNumber(addressData?.coordinate_lat)

    const longitude =
        toCoordinateNumber(addressData?.longitude) ??
        toCoordinateNumber(addressData?.lng) ??
        toCoordinateNumber(addressData?.long) ??
        toCoordinateNumber(addressData?.address_longitude) ??
        toCoordinateNumber(addressData?.coordinate_lng)

    if (latitude === null || longitude === null) return null
    if (!isValidCoordinate(latitude, longitude)) return null

    return { lat: latitude, lng: longitude }
}

const MapCenterUpdater = ({ center }) => {
    const map = useMap()

    useEffect(() => {
        if (center) {
            map.setView(center, map.getZoom())
        }
    }, [center, map])

    return null
}

const MapClickHandler = ({ onMapSelect }) => {
    useMapEvents({
        click(event) {
            onMapSelect(event.latlng.lat, event.latlng.lng)
        }
    })

    return null
}

function ProfileAddressForm() {
    const navigate = useNavigate()
    const { addressId } = useParams()
    const isEditMode = Boolean(addressId)

    const [isInitialLoading, setIsInitialLoading] = useState(isEditMode)
    const [isSaving, setIsSaving] = useState(false)
    const [isSearchingLocation, setIsSearchingLocation] = useState(false)
    const [isDetectingLocation, setIsDetectingLocation] = useState(false)
    const [locationKeyword, setLocationKeyword] = useState('')
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER)
    const [markerPosition, setMarkerPosition] = useState(DEFAULT_CENTER)

    const pageTitle = useMemo(
        () => (isEditMode ? 'Sunting Alamat' : 'Tambah Alamat'),
        [isEditMode]
    )

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors }
    } = useForm({
        defaultValues: {
            address_name: '',
            full_address: '',
            latitude: '',
            longitude: ''
        }
    })

    const setCoordinateFromMap = (latitude, longitude, shouldCenter = true) => {
        const latitudeNumber = toCoordinateNumber(latitude)
        const longitudeNumber = toCoordinateNumber(longitude)
        if (latitudeNumber === null || longitudeNumber === null) return
        if (!isValidCoordinate(latitudeNumber, longitudeNumber)) return

        const coordinate = { lat: latitudeNumber, lng: longitudeNumber }
        setMarkerPosition(coordinate)
        if (shouldCenter) {
            setMapCenter(coordinate)
        }

        setValue('latitude', latitudeNumber.toFixed(6), {
            shouldDirty: true,
            shouldValidate: true
        })
        setValue('longitude', longitudeNumber.toFixed(6), {
            shouldDirty: true,
            shouldValidate: true
        })
    }

    const getAddressDetail = async (selectedAddressId) => {
        const params = {
            access: 'auth',
            method: API.ADDRESS_GET.type,
            url: API.ADDRESS_GET.url,
            reqBody: {
                address_id: selectedAddressId
            }
        }

        const response = await Api(params)
        if (response.status) {
            if (Array.isArray(response.data)) return response.data[0] || null
            return response.data || null
        }

        const fallbackParams = {
            access: 'auth',
            method: API.ADDRESS.type,
            url: API.ADDRESS.url,
            reqBody: {}
        }

        const fallbackResponse = await Api(fallbackParams)
        if (!fallbackResponse.status || !Array.isArray(fallbackResponse.data)) {
            return null
        }

        return (
            fallbackResponse.data.find(
                (addressItem) => String(addressItem.address_id) === String(selectedAddressId)
            ) || null
        )
    }

    useEffect(() => {
        const init = async () => {
            if (!isEditMode) return

            setIsInitialLoading(true)
            try {
                const addressDetail = await getAddressDetail(addressId)
                if (!addressDetail) {
                    toast.error('Data alamat tidak ditemukan')
                    navigate('/profile')
                    return
                }

                setValue('address_name', addressDetail.address_name || '')
                setValue('full_address', addressDetail.full_address || '')

                const coordinate = getAddressCoordinate(addressDetail)
                if (coordinate) {
                    setMarkerPosition(coordinate)
                    setMapCenter(coordinate)
                    setValue('latitude', coordinate.lat.toFixed(6))
                    setValue('longitude', coordinate.lng.toFixed(6))
                }
            } catch (error) {
                toast.error(error.message || 'Gagal memuat data alamat')
            } finally {
                setIsInitialLoading(false)
            }
        }

        init()
    }, [addressId, isEditMode, navigate, setValue])

    const handleSearchLocation = async () => {
        const keyword = locationKeyword.trim()
        if (!keyword) {
            toast.error('Isi field Lokasi anda terlebih dahulu')
            return
        }

        setIsSearchingLocation(true)
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(
                    keyword
                )}`
            )
            if (!response.ok) {
                throw new Error('Gagal mencari lokasi')
            }

            const results = await response.json()
            if (!Array.isArray(results) || results.length === 0) {
                toast.error('Lokasi tidak ditemukan')
                return
            }

            const latitude = toCoordinateNumber(results[0].lat)
            const longitude = toCoordinateNumber(results[0].lon)
            if (latitude === null || longitude === null || !isValidCoordinate(latitude, longitude)) {
                toast.error('Koordinat lokasi tidak valid')
                return
            }

            setCoordinateFromMap(latitude, longitude, true)
            toast.success('Lokasi ditemukan')
        } catch (error) {
            toast.error(error.message || 'Gagal mencari lokasi')
        } finally {
            setIsSearchingLocation(false)
        }
    }

    const showLocationPrePromptDialog = async () => {
        const result = await Swal.fire({
            title: 'Gunakan lokasi saat ini?',
            text: 'Setelah ini browser akan meminta izin akses lokasi.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Lanjutkan',
            cancelButtonText: 'Batal'
        })

        return result.isConfirmed
    }

    const showLocationPermissionDeniedDialog = async () => {
        const result = await Swal.fire({
            title: 'Izin lokasi dibutuhkan',
            text: 'Izin lokasi ditolak. Aktifkan izin lokasi di browser, lalu coba lagi.',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Coba lagi',
            cancelButtonText: 'Nanti'
        })

        return result.isConfirmed
    }

    const requestCurrentLocation = () => {
        setIsDetectingLocation(true)
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                setCoordinateFromMap(position.coords.latitude, position.coords.longitude, true)
                toast.success('Lokasi saat ini berhasil digunakan')
                setIsDetectingLocation(false)
            },
            async (error) => {
                setIsDetectingLocation(false)

                if (error?.code === 1) {
                    const retry = await showLocationPermissionDeniedDialog()
                    if (retry) {
                        requestCurrentLocation()
                    }
                    return
                }

                toast.error('Lokasi tidak tersedia. Pastikan GPS/lokasi perangkat aktif.')
            },
            {
                enableHighAccuracy: true,
                timeout: 10000
            }
        )
    }

    const handleUseCurrentLocation = async () => {
        if (!navigator.geolocation) {
            toast.error('Browser tidak mendukung geolocation')
            return
        }

        if (isDetectingLocation) return

        const proceed = await showLocationPrePromptDialog()
        if (!proceed) return

        requestCurrentLocation()
    }

    const syncMapFromInput = () => {
        const latitude = toCoordinateNumber(getValues('latitude'))
        const longitude = toCoordinateNumber(getValues('longitude'))
        if (latitude === null || longitude === null) return
        if (!isValidCoordinate(latitude, longitude)) return
        setCoordinateFromMap(latitude, longitude, true)
    }

    const buildCoordinatePayload = (latitudeValue, longitudeValue) => {
        const latitude = toCoordinateNumber(latitudeValue)
        const longitude = toCoordinateNumber(longitudeValue)

        if (latitude === null && longitude === null) {
            return { coordinate: null, error: null }
        }

        if (latitude === null || longitude === null) {
            return {
                coordinate: null,
                error: 'Coordinate harus lengkap: isi latitude dan longitude, atau kosongkan keduanya.'
            }
        }

        if (!isValidCoordinate(latitude, longitude)) {
            return {
                coordinate: null,
                error: 'Coordinate tidak valid. Latitude: -90..90, Longitude: -180..180.'
            }
        }

        return {
            coordinate: `${latitude.toFixed(6)},${longitude.toFixed(6)}`,
            error: null
        }
    }

    const onSubmit = async (data) => {
        setIsSaving(true)

        const coordinateBuild = buildCoordinatePayload(data.latitude, data.longitude)
        if (coordinateBuild.error) {
            toast.error(coordinateBuild.error)
            setIsSaving(false)
            return
        }

        try {
            const reqBody = {
                address_name: data.address_name,
                full_address: data.full_address
            }

            if (coordinateBuild.coordinate) {
                reqBody.coordinate = coordinateBuild.coordinate
            }

            if (isEditMode) {
                reqBody.address_id = addressId
            }

            const params = {
                access: 'auth',
                method: isEditMode ? API.ADDRESS_EDIT.type : API.ADDRESS_CREATE.type,
                url: isEditMode ? API.ADDRESS_EDIT.url : API.ADDRESS_CREATE.url,
                reqBody
            }

            const response = await Api(params)
            if (response.status) {
                toast(response.message || 'Alamat berhasil disimpan')
                setTimeout(() => {
                    navigate('/profile')
                }, 400)
            } else {
                toast.error(response.message || 'Gagal menyimpan alamat')
            }
        } catch (error) {
            toast.error(error.message || 'Gagal menyimpan alamat')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            <div className="mx-auto max-w-sm px-4 py-5 pb-24">
                <div className="mb-4 flex items-center justify-between">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-1 text-xs font-normal"
                        onClick={() => navigate('/profile')}
                    >
                        <AiOutlineArrowLeft className="text-base" />
                        Kembali
                    </button>
                    <h1 className="text-sm font-semibold text-slate-800">{pageTitle}</h1>
                    <span className="w-14" />
                </div>

                <Card className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Card.Body className="p-4">
                        {isInitialLoading ? (
                            <div className="animate-pulse space-y-3">
                                <div className="h-10 rounded-md bg-gray-200" />
                                <div className="h-20 rounded-md bg-gray-200" />
                                <div className="h-12 rounded-md bg-gray-200" />
                                <div className="h-56 rounded-md bg-gray-200" />
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                <div>
                                    <label className="label">
                                        <span className="label-text font-medium">Nama Alamat</span>
                                    </label>
                                    <input
                                        className="input input-bordered w-full focus:input-primary"
                                        placeholder="Contoh: Rumah, Kantor"
                                        {...register('address_name', { required: true })}
                                    />
                                    {errors.address_name && (
                                        <span className="text-sm text-red-500">Nama alamat wajib diisi</span>
                                    )}
                                </div>

                                <div>
                                    <label className="label">
                                        <span className="label-text font-medium">Alamat Lengkap</span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered h-28 w-full focus:textarea-primary"
                                        placeholder="Tulis alamat lengkap secara manual"
                                        {...register('full_address', { required: true })}
                                    />
                                    {errors.full_address && (
                                        <span className="text-sm text-red-500">Alamat lengkap wajib diisi</span>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <label className="label py-0">
                                            <span className="label-text font-medium">Lokasi anda</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleUseCurrentLocation}
                                            className="text-xs font-normal text-blue-600 hover:underline"
                                            disabled={isDetectingLocation}
                                        >
                                            {isDetectingLocation
                                                ? 'Mendeteksi lokasi...'
                                                : 'Gunakan lokasi saat ini'}
                                        </button>
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="Cari lokasi, contoh: Monas Jakarta"
                                            value={locationKeyword}
                                            onChange={(event) => setLocationKeyword(event.target.value)}
                                        />
                                        <Button
                                            type="button"
                                            onClick={handleSearchLocation}
                                            className="rounded-md border-none bg-slate-700 px-3 text-white hover:bg-slate-800"
                                            disabled={isSearchingLocation}
                                        >
                                            {isSearchingLocation ? (
                                                <AiOutlineLoading3Quarters className="animate-spin text-base" />
                                            ) : (
                                                <AiOutlineSearch className="text-base" />
                                            )}
                                        </Button>
                                    </div>
                                </div>

                                <div className="relative z-0 overflow-hidden rounded-md border border-slate-200">
                                    <MapContainer
                                        center={mapCenter}
                                        zoom={DEFAULT_ZOOM}
                                        scrollWheelZoom
                                        className="relative z-0 h-56 w-full"
                                    >
                                        <TileLayer
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        />
                                        <MapCenterUpdater center={mapCenter} />
                                        <MapClickHandler onMapSelect={setCoordinateFromMap} />
                                        {markerPosition && (
                                            <Marker
                                                position={markerPosition}
                                                draggable
                                                eventHandlers={{
                                                    dragend: (event) => {
                                                        const newCoordinate = event.target.getLatLng()
                                                        setCoordinateFromMap(
                                                            newCoordinate.lat,
                                                            newCoordinate.lng,
                                                            false
                                                        )
                                                    }
                                                }}
                                            />
                                        )}
                                    </MapContainer>
                                </div>
                                <p className="flex items-start gap-1 text-xs text-slate-500">
                                    <AiOutlineEnvironment className="mt-0.5 text-sm" />
                                    Coordinate opsional. Klik/geser marker jika ingin menambahkan titik lokasi.
                                </p>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div>
                                        <label className="label">
                                            <span className="label-text font-medium">Latitude (opsional)</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="any"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="-6.200000"
                                            {...register('latitude', {
                                                validate: (value) => {
                                                    if (value === '' || value === null || value === undefined)
                                                        return true
                                                    const numericValue = Number(value)
                                                    if (Number.isNaN(numericValue)) {
                                                        return 'Latitude harus angka'
                                                    }
                                                    if (numericValue < -90 || numericValue > 90) {
                                                        return 'Latitude harus di rentang -90 sampai 90'
                                                    }
                                                    return true
                                                }
                                            })}
                                            onBlur={syncMapFromInput}
                                        />
                                        {errors.latitude && (
                                            <span className="text-sm text-red-500">
                                                {errors.latitude.message}
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <label className="label">
                                            <span className="label-text font-medium">Longitude (opsional)</span>
                                        </label>
                                        <input
                                            type="number"
                                            step="any"
                                            className="input input-bordered w-full focus:input-primary"
                                            placeholder="106.816666"
                                            {...register('longitude', {
                                                validate: (value) => {
                                                    if (value === '' || value === null || value === undefined)
                                                        return true
                                                    const numericValue = Number(value)
                                                    if (Number.isNaN(numericValue)) {
                                                        return 'Longitude harus angka'
                                                    }
                                                    if (numericValue < -180 || numericValue > 180) {
                                                        return 'Longitude harus di rentang -180 sampai 180'
                                                    }
                                                    return true
                                                }
                                            })}
                                            onBlur={syncMapFromInput}
                                        />
                                        {errors.longitude && (
                                            <span className="text-sm text-red-500">
                                                {errors.longitude.message}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 pt-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="text-xs font-normal"
                                        onClick={() => navigate('/profile')}
                                    >
                                        Batal
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="border-none bg-slate-700 text-xs font-normal text-white hover:bg-slate-800"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Menyimpan...' : 'Simpan'}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    )
}

export default ProfileAddressForm
